<script lang="ts">
	//Services
	import { format } from '../../../../../services/i18n';
	import { postReq } from '../../../../../services/http';

	//Stores
	import { getDeviceStatus, getDeviceSettings } from 'src/stores/device-runtime/status-selectors';

	//Types
	import type { SettingRequest } from '../../../../../types/requests/settings/setting';
	import type { Device } from '../../../../../types/Device';

	//Helpers
	import { onSettingsResponse } from '../../../../../helpers/device_settings_helpers';

	//Design system components
	import { Button, Checkbox, Select, Text } from '@Shelly/shelly-design-system';
	import { getContext, onDestroy } from 'svelte';
	import type { Writable } from 'svelte/store';
	import { showNewLayout } from 'src/stores/toDelete/new_layout';
	import type { SidepanelStickyBottomProps } from 'src/components/Sidepanel/sidepanel-types';

	export let device: Device;

	const options = {
		1: $format('option_low'),
		4: $format('option_normal'),
		7: $format('option_high'),
	};

	$: settingsStore = getDeviceSettings(device.id);
	$: settings = $settingsStore;

	let selected_index = settings.display.brightness < 4 ? 1 : settings.display.brightness < 7 ? 4 : 7;
	let selected = options[selected_index];
	let cacheSelected = selected;
	let rotated = settings.display.flipped;
	let cacheRotate = rotated;
	let loading = false;

	let brightness;

	const setDimmingType = (e): void => {
		brightness = e.detail;
		if (brightness === 'Low') {
			selected_index = 1;
		} else if (brightness === 'Normal') {
			selected_index = 4;
		} else if (brightness === 'High') {
			selected_index = 7;
		}
	};

	const setRotate = async () => {
		rotated = !rotated;
	};
	const sendRequest = async () => {
		if (cacheRotate === rotated && cacheSelected === selected) {
			return;
		} else {
			const saveRotation = async (): Promise<SettingRequest> => {
				if (cacheRotate === rotated) {
					return { isok: true };
				}
				loading = true;
				cacheRotate = rotated;
				return await postReq(
					`/device/generic_command`,
					{
						id: device.id,
						command: 'display',
						payload: JSON.stringify({ flipped: rotated }),
					},
					20000,
				);
			};
			const saveBrightness = async (): Promise<SettingRequest> => {
				if (cacheSelected === selected) {
					return { isok: true };
				}
				loading = true;
				cacheSelected = selected;
				return await postReq(
					`/device/generic_command`,
					{
						id: device.id,
						command: 'display',
						payload: JSON.stringify({ brightness: selected_index }),
					},
					20000,
				);
			};
			let res: SettingRequest[] = await Promise.all([saveRotation(), saveBrightness()]);
			onSettingsResponse(res, device);
			loading = false;
		}
	};

	const stickyBottomAction =
		getContext<Writable<SidepanelStickyBottomProps | null>>('stickyBottomAction');

	$: if (stickyBottomAction) {
		if ($showNewLayout) {
			stickyBottomAction.set({ onClick: sendRequest, loading, disabled: loading });
		} else {
			stickyBottomAction.set(null);
		}
	}

	onDestroy(() => {
		stickyBottomAction?.set(null);
	});
</script>

<Text styleType={'body-small'} class="text-small mb-s"
	>{$format('dimming_type', { default: 'Dimming type:' })}</Text
>

<Checkbox size="m" class="mb-s" checked={rotated} on:click={setRotate} id="checkbox-jj9t50i"
	>{$format('rotate_screen_label') || 'Rotate screen.'}</Checkbox
>
<Select
	size="m"
	bind:value={selected}
	options={Object.values(options)}
	icon={'fas fa-brightness'}
	on:change={setDimmingType}
	id="select-ebc20jo"
	aria-label={$format('dimming_type', { default: 'Dimming type' })}
/>

{#if !$showNewLayout}
	<Button {loading} size="m" width="full" class="mt-s" on:click={sendRequest}>
		{$format('button_save')}
	</Button>
{/if}
